import { DateTime, Interval } from "luxon";

import { t2m_colormap, td2m_colormap, spd500mb_colormap, spd850mb_colormap, cape_colormap } from "../Colormap.js";
import { aggregateFields, FieldContourFill, FieldContour, FieldBarbs, FieldHodographs } from "./EnvironmentPlot.js";
import EnvironmentLayer from "./EnvironmentLayer.js";
import {randomChoice} from '../utils.js';

const CACHE_FMT = 'yyyy/MM/dd/HH';
const REMOTE_FMT = CACHE_FMT;


const ENV_LAYERS = {
    't2m': {
        'layers': [
            {'type': FieldContourFill, 'id': 't2m', 'fields': ['t2m'], 'args': {'cmap': t2m_colormap}},
            {'type': FieldContour, 'id': 'pmsl', 'fields': ['pmsl'], 'args': {'interval': 2, 'color': '#000000'}},
            {'type': FieldBarbs, 'id': 'barb10m', 'fields': ['u10m', 'v10m'], 'args': {'color': '#000000'},
            'func': (u, v) => { return {'u': u, 'v': v}; }},
        ],
        'title': 'ERA5 2 m Temperature, MSLP, and 10 m Wind Barbs (kts)',
        'short-title': 'ERA5 2m T, MSLP, 10 m Winds',
        'dropdown': '2 m Temperature/MSLP/10 m Winds'
    },
    'td2m': {
        'layers': [
            {'type': FieldContourFill, 'id': 'td2m', 'fields': ['td2m'], 'args': {'cmap': td2m_colormap}},
            {'type': FieldContour, 'id': 'pmsl', 'fields': ['pmsl'], 'args': {'interval': 2, 'color': '#000000'}},
            {'type': FieldBarbs, 'id': 'barb10m', 'fields': ['u10m', 'v10m'], 'args': {'color': '#000000'},
             'func': (u, v) => { return {'u': u, 'v': v}; }}
        ],
        'title': 'ERA5 2 m Dewpoint, MSLP, and 10 m Wind Barbs (kts)',
        'short-title': 'ERA5 2 m Td, MSLP, 10 m Winds',
        'dropdown': '2 m Dewpoint/MSLP/10 m Winds'
    },
    'spd500mb': {
        'layers': [
            {'type': FieldContourFill, 'id': 'spd500mb', 'fields': ['u500mb', 'v500mb'], 'args': {'cmap': spd500mb_colormap},
             'func': (u, v) => aggregateFields(Math.hypot, u, v)},
            {'type': FieldContour, 'id': 'height500mb', 'fields': ['height500mb'], 'args': {'interval': 30, 'color': '#000000'}},
            {'type': FieldBarbs, 'id': 'barb500mb', 'fields': ['u500mb', 'v500mb'], 'args': {'color': '#000000'},
             'func': (u, v) => { return {'u': u, 'v': v}; }},
        ],
        'title': 'ERA5 500 mb Wind Speed, Height, and Wind Barbs (kts)',
        'short-title': 'ERA5 500 mb Winds, Heights',
        'dropdown': '500 mb Wind/Height'
    },
    'spd850mb': {
        'layers': [
            {'type': FieldContourFill, 'id': 'spd850mb', 'fields': ['u850mb', 'v850mb'], 'args': {'cmap': spd850mb_colormap},
             'func': (u, v) => aggregateFields(Math.hypot, u, v)},
            {'type': FieldContour, 'id': 'height850mb', 'fields': ['height850mb'], 'args': {'interval': 15, 'color': '#000000'}},
            {'type': FieldBarbs, 'id': 'barb850mb', 'fields': ['u850mb', 'v850mb'], 'args': {'color': '#000000'},
             'func': (u, v) => { return {'u': u, 'v': v}; }},
        ],
        'title': 'ERA5 850 mb Wind Speed, Height, and Wind Barbs (kts)',
        'short-title': 'ERA5 850 mb Winds, Heights',
        'dropdown': '850 mb Wind/Height'
    },
    'hodocape': {
        'layers': [
            {'type': FieldContourFill, 'id': 'cape', 'fields': ['cape'], 'args': {'cmap': cape_colormap}},
            {'type': FieldBarbs, 'id': 'barb10m', 'fields': ['u10m', 'v10m'], 'args': {'color': '#505050'},
             'func': (u, v) => { return {'u': u, 'v': v}; }},
            {'type': FieldHodographs, 'id': 'hodographs', 'fields': ['hodographs'], 'args': {'bgcolor': '#3a3a3a'}}
        ],
        'title': 'ERA5 SBCAPE and Hodographs',
        'short-title': 'ERA5 SBCAPE, Hodographs',
        'dropdown': 'SBCAPE/Hodographs'
    }
};

const delay = delay_dt => {
    return new Promise((resolve, reject) => {
        window.setTimeout(resolve, delay_dt);
    });
}

class EnvironmentDataHandler {
    constructor(base_path) {
        this.base_path = base_path;

        this.available_dates = [];
        this.available_date_strs = [];
    }

    getAvailableDates(domain, source, callback) {
        const now = DateTime.utc();
        fetch(this.base_path + '/get_env_dates.php?dom=' + domain + '&src=' + source + '&' + now.toISO())
            .then(resp => resp.json())
            .then(data => {
                this.available_date_strs = data['available_dates'];
                this.available_dates = this.available_date_strs.map(dt => DateTime.fromFormat(dt, 'yyyy-MM-dd', {'zone': 'utc'}))
            }).then(callback);
    }

    hasData(dt, cday_offset) {
        return this.available_date_strs.includes(dt.minus(cday_offset).toFormat('yyyy-MM-dd'));
    }

    availableDaysInIntervals(intvs, cday_offset) {
        const intvs_shift = intvs.map(intv => Interval.fromDateTimes(intv.start.minus(cday_offset), intv.end.minus(cday_offset)));
        const full_intv = Interval.merge(intvs_shift)[0];
        const dates_in_full_intv = this.available_dates.filter(dt => full_intv.contains(dt));

        return intvs_shift.map(intv_shift => dates_in_full_intv.map(dt => intv_shift.contains(dt) ? 1 : 0).reduce((a, b) => a + b, 0));
    }

    getRandomDate() {
        return randomChoice(this.available_dates);
    }

    getLayers(type) {
        const lyr_specs = ENV_LAYERS[type]['layers'];
        return lyr_specs.map(lyr_spec => new EnvironmentLayer(lyr_spec));
    }

    populateLayers(layers, datetimes, default_dt, domain, source) {
        if (layers.length == 0) {
            return;
        }
        const dt_from_default = dt => Math.abs(dt.toMillis() - default_dt.toMillis());
        datetimes.sort((a, b) => dt_from_default(a) - dt_from_default(b));

        const layer_dts_millis = layers[0].getDatetimes().map(dt => dt.toMillis());
        datetimes = datetimes.filter(dt => !layer_dts_millis.includes(dt.toMillis()))

        if (datetimes.length == 0) {
            return;
        }
        console.log("Downloading layers for " + datetimes.length + (datetimes.length == 1 ? ' time' : ' times'));

        const lyr_specs = layers.map(lyr => lyr.spec);

        // Uniquify fields and only download fields once, even if they're mentioned multiple times (like 500mb u and v)
        let fields = lyr_specs.map(lyr_spec => lyr_spec['fields']).flat();
        fields = [...new Set(fields)];

        // Pull the download functions out of the layer classes
        let downloaders = {};
        lyr_specs.forEach(lyr_spec => {
            lyr_spec['fields'].forEach(field_name => {
                downloaders[field_name] = lyr_spec['type'].downloader;
            })
        })

        datetimes.forEach(async dt => {
            // Add null fields to tell the layers to expect fields, but they're not downloaded yet
            layers.forEach(lyr => {
                lyr.addField(null, dt);
            });

            // Do downloads
            const promises = fields.map(field_name => {
                const downloader = downloaders[field_name];
                const rem_fname = this.base_path + '/' + domain + '/' + source + '/' + dt.toFormat(REMOTE_FMT) + '/' + field_name + '.dat';
                return downloader(rem_fname);
            });

            // Handle downloaded data
            Promise.allSettled(promises).then(results => {
                let result_vals = Object.fromEntries(results.map((res, ires) => [fields[ires], res]));

                Object.entries(result_vals).forEach(([k, v]) => {
                    if (v['status'] == 'rejected') {
                        console.error('Download for ' + k + ' failed: ' + v['reason'])
                    }
                })
                result_vals = Object.fromEntries(Object.entries(result_vals).filter(([k, v]) => v['status'] != 'rejected'));

                lyr_specs.forEach((lyr_spec, ilyr) => {
                    const lyr_fields = lyr_spec['fields'].map(field_name => result_vals[field_name])
                                                         .filter(val => val !== undefined).map(res => res['value']);

                    if (lyr_fields.length < lyr_spec['fields'].length) {
                        // If we don't have all the fields we expect, bail out
                        return;
                    }

                    let args = [];
                    if (lyr_spec.hasOwnProperty('func')) {
                        const agg_field = lyr_spec['func'](...lyr_fields);
                        args.push(agg_field);
                    }
                    else {
                        args.push(lyr_fields[0]);
                    }

                    if (lyr_spec.hasOwnProperty('args')) {
                        args.push(lyr_spec['args']);
                    }

                    const field = new lyr_spec['type'](...args);
                    layers[ilyr].addField(field, dt);
                });
            });

            await delay(50);
        });

        return layers;
    }

    getColormaps(type) {
        if (type === null) {
            return [];
        }

        return ENV_LAYERS[type]['layers'].map(lyr_spec => lyr_spec['args'])
            .filter(args => args != undefined).map(args => args['cmap']).filter(cmap => cmap !== undefined);
    }

    getTitle(type, which) {
        if (which === undefined) {
            which = 'normal';
        }

        let title;
        if (which == 'short') {
            title = ENV_LAYERS[type]['short-title'];
        }
        else {
            title = ENV_LAYERS[type]['title'];
        }
        return title;
    }

    getAvailableTypes() {
        return Object.keys(ENV_LAYERS);
    }

    getAvailableReanalyses() {
        return ['ERA5'];
    }

    getDropdownEntry(type) {
        return ENV_LAYERS[type]['dropdown'];
    }
}

export default EnvironmentDataHandler;